import * as tus from "tus-js-client";
import { useToast } from "@chakra-ui/react";
import { useApolloClient } from "@apollo/client";
import { vimeoUploadUrl, generateVideoThumbnail } from "../utils/helper";
import { useNotification } from "../contexts/NotificationProvider";
import { useContext } from "react";
import { PublisherAndUserContext } from "../contexts/PublisherAndUserContext";

export const useVideoUploader = () => {
  const {showNotification, hideNotification} = useNotification();
  const { publisher } = useContext(PublisherAndUserContext);

  const apolloClient = useApolloClient();
  const toast = useToast();

  const uploadVideo = async (data, uploadProgressCallback = () => {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            const {title, description = "", tags = [], language = "en", videoFile} = data
            const tempId = `temp-${Date.now()}`;
            const video = {
                id: tempId,
                url: "",
                name: title,
                duration: 19,
                thumbnail: "",
                description: description,
                tags: tags,
                status: "uploading",
                uploadProgress: 0,
            }
            apolloClient.cache.modify({
                fields: {
                    edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                        const updatedVideos = [
                            video,
                            ...existingedshopifyVideosRefs.videos,
                        ];
        
                        return {
                            ...existingedshopifyVideosRefs,
                            videos: updatedVideos,
                        };
                    },
                },
            });
            const [vUploadUrl, thumbnail] = await Promise.allSettled([vimeoUploadUrl({
                id: publisher?.id, 
                size: videoFile.size,
                title,
                description,
                language,
                tags
            }), generateVideoThumbnail(videoFile)])
            if(vUploadUrl?.status === "rejected") {
                reject(vUploadUrl?.reason)
            }
            if(thumbnail?.status === "rejected") {
                reject(thumbnail?.reason)
            }
            const {id, uploadLink} = vUploadUrl?.value
            apolloClient.cache.modify({
                fields: {
                    edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                        return {
                            ...existingedshopifyVideosRefs,
                            videos: existingedshopifyVideosRefs.videos.map((v) =>
                                readField("id", v) === tempId ? { ...v, id, url: `https://vimeo.com/${id}`, thumbnail: thumbnail?.value } : v
                            ),
                        };
                    },
                },
            });
            const upload = new tus.Upload(videoFile, {
              uploadUrl: uploadLink, // Endpoint is the upload link
              chunkSize: 5 * 1024 * 1024, // 5 MB chunks
              retryDelays: [0, 1000, 3000, 5000],
              metadata: {
                filename: videoFile.name,
                filetype: videoFile.type,
                videoName: videoFile.name
              },
              beforeRequest: (req) => {
                // Ensure the method is PATCH (though it should be by default)
                req.method = 'PATCH';
              },
              onError(error) {
                console.error("Upload failed:", error);
                showNotification('error', 'Failed to upload video. Please try again.');
                apolloClient.cache.modify({
                    fields: {
                        edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                            return {
                                ...existingedshopifyVideosRefs,
                                videos: existingedshopifyVideosRefs.videos.filter((v) =>
                                    readField("id", v) !== id
                                ),
                            };
                        },
                    },
                });
                hideNotification();
              },
              onProgress(bytesUploaded, bytesTotal) {
                const progress = Math.floor((bytesUploaded / bytesTotal) * 100);
                uploadProgressCallback(id, progress)
                apolloClient.cache.modify({
                    fields: {
                        edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                            return {
                                ...existingedshopifyVideosRefs,
                                videos: existingedshopifyVideosRefs.videos.map((v) =>
                                    readField("id", v) === id ? { ...v, uploadProgress: progress} : v
                                ),
                            };
                        },
                    },
                });
              },
              onSuccess: async () => {
                apolloClient.cache.modify({
                    fields: {
                        edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                            return {
                                ...existingedshopifyVideosRefs,
                                videos: existingedshopifyVideosRefs.videos.map((v) =>
                                    readField("id", v) === id ? { ...v, uploadProgress: 100} : v
                                ),
                            };
                        },
                    },
                });
              },
            });
        
            upload.start();
            resolve("Uploaded successfully");
        } catch (e) {
          reject(e);
        }
    })

  };

  return {
    uploadVideo
  };
}
